'use server'

import { apiRequest, ApiResponse } from '../request'

export type PipelineType = 'ACQUISITION' | 'RETENTION' | 'UPSELL' | 'CUSTOM'

/** Canal de aquisição do funil padrão do tráfego pago. */
export type PaidTrafficChannel = 'META' | 'GOOGLE' | 'TIKTOK' | 'CAMPAIGN'

export const PAID_TRAFFIC_CHANNELS: PaidTrafficChannel[] = ['META', 'GOOGLE', 'TIKTOK', 'CAMPAIGN']

export const PAID_TRAFFIC_CHANNEL_LABELS: Record<PaidTrafficChannel, string> = {
    META: 'Meta Ads',
    GOOGLE: 'Google Ads',
    TIKTOK: 'TikTok Ads',
    CAMPAIGN: 'Campanhas próprias',
}

/** Canais do funil, com a marcação antiga (todos os canais) resolvida. */
export function pipelinePaidTrafficChannels(pipeline: {
    paidTrafficChannels?: PaidTrafficChannel[] | null
    isPaidTrafficDefault?: boolean | null
}): PaidTrafficChannel[] {
    if (pipeline.paidTrafficChannels?.length) return pipeline.paidTrafficChannels
    return pipeline.isPaidTrafficDefault ? [...PAID_TRAFFIC_CHANNELS] : []
}

export interface PipelineStage {
    id: number
    pipelineId: number
    name: string
    description?: string
    color?: string
    sortOrder: number
    probability: number
    isWonStage: boolean
    isLostStage: boolean
    /** Oculta para a IA: o agente não vê a etapa nem move card para ela ou para fora dela. */
    hiddenFromAi?: boolean
    rottingDays?: number
    createdAt: string
    updatedAt: string
    _count?: {
        deals: number
    }
}

export interface SalesPipeline {
    id: number
    /** Dono do funil — docs/sales-org-pipelines-and-message-routing.md. */
    organizationId?: number | null
    /** Filial do funil. Nula = funil da organização inteira (triagem, follow-up, overflow). */
    companyId: number | null
    company?: { id: number; name: string } | null
    name: string
    /** Quem entra neste funil. A IA usa este texto para escolher o funil de cada contato. */
    description?: string
    type: PipelineType
    /** Padrão da ORGANIZAÇÃO — a triagem, onde nasce todo contato novo. */
    isDefault: boolean
    /** Padrão da FILIAL — onde nasce a oportunidade de quem já foi atendido nela. */
    isUnitDefault?: boolean
    /** LEGADO: padrão do tráfego pago para todos os canais (vale enquanto a lista estiver vazia). */
    isPaidTrafficDefault?: boolean
    /** Canais em que este funil é o padrão do tráfego pago. */
    paidTrafficChannels?: PaidTrafficChannel[]
    allowMultipleDeals: boolean
    active: boolean
    createdAt: string
    updatedAt: string
    stages?: PipelineStage[]
    _count?: {
        stages: number
        deals: number
    }
    // Movimentação automática (docs/sales-stage-routing.md)
    appointmentStageId?: number | null
    budgetStageId?: number | null
    budgetApprovedStageId?: number | null
    paidStageId?: number | null
    // Distribuição (docs/sales-pipeline-distribution.md)
    /** Dona do funil: vira responsável pela oportunidade distribuída a ele. */
    ownerUserId?: number | null
    owner?: { id: number; name: string } | null
    /** Funis do mesmo grupo dividem as oportunidades que entram automaticamente. */
    distributionGroupId?: number | null
    distributionGroup?: { id: number; name: string } | null
    lastDistributedAt?: string | null
    /** Perfis que veem o funil. Vazio e sem dona = só administradores e perfis que veem todos os funis. */
    roleAccess?: Array<{ roleId: number; role: { id: number; name: string } }>
}

export interface PipelineDistributionGroup {
    id: number
    name: string
    organizationId: number
    createdAt: string
    updatedAt: string
    _count: {
        pipelines: number
    }
}

export interface PipelineOwnerCandidate {
    id: number
    name: string
    email: string
}

export interface CreatePipelineData {
    name: string
    description?: string
    type?: PipelineType
    /** Filial do funil (opcional). Nula = funil da organização inteira. */
    companyId?: number | null
    isDefault?: boolean
    isUnitDefault?: boolean
    /** Canais em que este funil é o padrão do tráfego pago (um funil por canal na organização). */
    paidTrafficChannels?: PaidTrafficChannel[]
    allowMultipleDeals?: boolean
    stages?: CreateStageData[]
}

export interface UpdatePipelineData {
    name?: string
    description?: string
    type?: PipelineType
    companyId?: number | null
    isDefault?: boolean
    isUnitDefault?: boolean
    /** Canais em que este funil é o padrão do tráfego pago (um funil por canal na organização). */
    paidTrafficChannels?: PaidTrafficChannel[]
    allowMultipleDeals?: boolean
    active?: boolean
    appointmentStageId?: number | null
    budgetStageId?: number | null
    budgetApprovedStageId?: number | null
    paidStageId?: number | null
    ownerUserId?: number | null
    distributionGroupId?: number | null
    /** Lista vazia remove a restrição por perfil. */
    roleIds?: number[]
}

export type PipelineRoutingField =
    | 'appointmentStageId'
    | 'budgetStageId'
    | 'budgetApprovedStageId'
    | 'paidStageId'

export type PipelineRouting = Record<PipelineRoutingField, number | null>

export interface PipelineRoutingFieldConfig {
    field: PipelineRoutingField
    label: string
    help: string
    /** Entrada: o card NASCE nesta etapa (vazio = primeira). Movimento: o card AVANÇA (vazio = não move). */
    kind: 'entry' | 'move'
}

export const PIPELINE_ROUTING_FIELDS: PipelineRoutingFieldConfig[] = [
    {
        field: 'appointmentStageId',
        label: 'Agendamento criado',
        help: 'Etapa em que o card nasce quando um atendimento é agendado.',
        kind: 'entry',
    },
    {
        field: 'budgetStageId',
        label: 'Orçamento criado',
        help: 'Etapa em que o card nasce quando um orçamento é emitido.',
        kind: 'entry',
    },
    {
        field: 'budgetApprovedStageId',
        label: 'Orçamento aprovado',
        help: 'Para onde o card vai quando o cliente aprova o orçamento.',
        kind: 'move',
    },
    {
        field: 'paidStageId',
        label: 'Cobrança paga',
        help: 'Para onde o card vai quando a cobrança vinculada é paga.',
        kind: 'move',
    },
]

export interface PipelineRoutingOwner {
    /** Automação que assume a cobrança paga deste funil — quando existe, ela vence o mapeamento. */
    paid: { id: number; name: string } | null
}

export interface CreateStageData {
    name: string
    description?: string
    color?: string
    sortOrder?: number
    probability?: number
    isWonStage?: boolean
    isLostStage?: boolean
    hiddenFromAi?: boolean
    rottingDays?: number
}

export interface UpdateStageData {
    name?: string
    description?: string
    color?: string
    sortOrder?: number
    probability?: number
    isWonStage?: boolean
    isLostStage?: boolean
    hiddenFromAi?: boolean
    rottingDays?: number
}

// Pipelines

/**
 * Funis que podem RECEBER uma oportunidade. Diferente de `getPipelines`, não sofre o
 * recorte de visibilidade (dona e perfis): quem tem permissão de mover precisa enxergar
 * o destino, mesmo sem acompanhar aquele funil no quadro.
 */
export interface PipelineMoveTarget {
    id: number
    name: string
    isDefault: boolean
    companyId: number | null
    company?: { id: number; name: string } | null
    stages: Array<{
        id: number
        name: string
        sortOrder: number
        isWonStage: boolean
        isLostStage: boolean
    }>
}

export async function getPipelineMoveTargets(): Promise<ApiResponse<PipelineMoveTarget[]>> {
    return apiRequest<PipelineMoveTarget[]>('sales/pipelines/move-targets', {}, 'get')
}

export async function getPipelines(): Promise<ApiResponse<SalesPipeline[]>> {
    return apiRequest<SalesPipeline[]>('sales/pipelines', {})
}

export async function getPipeline(id: number): Promise<ApiResponse<SalesPipeline>> {
    return apiRequest<SalesPipeline>(`sales/pipelines/${id}`, {})
}

export async function createPipeline(
    data: CreatePipelineData
): Promise<ApiResponse<SalesPipeline>> {
    return apiRequest<SalesPipeline>('sales/pipelines', { data }, 'post')
}

export async function updatePipeline(
    id: number,
    data: UpdatePipelineData
): Promise<ApiResponse<SalesPipeline>> {
    return apiRequest<SalesPipeline>(`sales/pipelines/${id}`, { data }, 'patch')
}

export async function getPipelineRoutingOwner(
    id: number
): Promise<ApiResponse<PipelineRoutingOwner>> {
    return apiRequest<PipelineRoutingOwner>(`sales/pipelines/${id}/routing-owner`, {})
}

export async function deletePipeline(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`sales/pipelines/${id}`, {}, 'delete')
}

// Distribuição (docs/sales-pipeline-distribution.md)

export async function getPipelineOwnerCandidates(): Promise<
    ApiResponse<PipelineOwnerCandidate[]>
> {
    return apiRequest<PipelineOwnerCandidate[]>(
        'sales/pipelines/owner-candidates',
        {}
    )
}

export async function getPipelineDistributionGroups(): Promise<
    ApiResponse<PipelineDistributionGroup[]>
> {
    return apiRequest<PipelineDistributionGroup[]>(
        'sales/pipelines/distribution-groups',
        {}
    )
}

export async function createPipelineDistributionGroup(data: {
    name: string
}): Promise<ApiResponse<PipelineDistributionGroup>> {
    return apiRequest<PipelineDistributionGroup>(
        'sales/pipelines/distribution-groups',
        { data },
        'post'
    )
}

export async function updatePipelineDistributionGroup(
    groupId: number,
    data: { name: string }
): Promise<ApiResponse<PipelineDistributionGroup>> {
    return apiRequest<PipelineDistributionGroup>(
        `sales/pipelines/distribution-groups/${groupId}`,
        { data },
        'patch'
    )
}

export async function deletePipelineDistributionGroup(
    groupId: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(
        `sales/pipelines/distribution-groups/${groupId}`,
        {},
        'delete'
    )
}

// Stages

export async function createStage(
    pipelineId: number,
    data: CreateStageData
): Promise<ApiResponse<PipelineStage>> {
    return apiRequest<PipelineStage>(
        `sales/pipelines/${pipelineId}/stages`,
        { data },
        'post'
    )
}

export async function updateStage(
    pipelineId: number,
    stageId: number,
    data: UpdateStageData
): Promise<ApiResponse<PipelineStage>> {
    return apiRequest<PipelineStage>(
        `sales/pipelines/${pipelineId}/stages/${stageId}`,
        { data },
        'patch'
    )
}

export async function deleteStage(
    pipelineId: number,
    stageId: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(
        `sales/pipelines/${pipelineId}/stages/${stageId}`,
        {},
        'delete'
    )
}

export async function reorderStages(
    pipelineId: number,
    stageIds: number[]
): Promise<ApiResponse<void>> {
    return apiRequest<void>(
        `sales/pipelines/${pipelineId}/stages/reorder`,
        { data: { stageIds } },
        'patch'
    )
}

// Labels

export const PIPELINE_TYPE_LABELS: Record<PipelineType, string> = {
    ACQUISITION: 'Aquisição',
    RETENTION: 'Retenção',
    UPSELL: 'Upsell',
    CUSTOM: 'Personalizado',
}
